/**
 * Knoblauch — Priester (Cleric). LMAO-Style (BATTLE-14).
 * Persoenlichkeit: "sanfter Beschuetzer" — die Papierhuelle ist seine ROBE:
 * mehrlagig (hinterer Papier-Mantel mit Zipfeln + Haupt-Robe mit Scallop-Saum),
 * oben ein kleiner Spross als Tonsur/Heiligenschein-Traeger.
 * Licht: warm & weich (doppeltes Rim-Light in Gold). Sternstufen: 2★ Kordel-Guertel
 * mit Kreuz-Brosche + Saum-Borte, 3★ goldener Heiligenschein + Segens-Runen.
 * Referenz-Qualitaet: kohlrabi.ts. Contract: species/types.ts.
 */
import type { PaintParams, SpeciesPainter } from './types'
import { darken, lighten, rgba, rimLight, specular } from '../draw3d'

const HOLY_GOLD = '#ffd94a' // Heilig-Gold, konsistent mit kohlrabi-Runen / KEYWORD holy

/** Haupt-Robe: Zwiebelform mit aufgefaechertem Scallop-Saum (Papier-Lagen) */
function robePath(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath()
  ctx.moveTo(x, y - r * 1.06)
  // rechte Flanke: sanft bauchig (weich, nicht kantig — "sanfter Beschuetzer")
  ctx.quadraticCurveTo(x + r * 0.3, y - r * 0.76, x + r * 0.8, y - r * 0.12)
  ctx.quadraticCurveTo(x + r * 0.98, y + r * 0.48, x + r * 0.64, y + r * 0.82)
  // Saum: 3 nach unten gewoelbte Papier-Scallops (leicht asymmetrisch, deterministisch)
  ctx.quadraticCurveTo(x + r * 0.42, y + r * 0.97, x + r * 0.22, y + r * 0.83)
  ctx.quadraticCurveTo(x + r * 0.01, y + r * 0.99, x - r * 0.2, y + r * 0.84)
  ctx.quadraticCurveTo(x - r * 0.43, y + r * 0.96, x - r * 0.64, y + r * 0.82)
  // linke Flanke hoch zum Scheitel
  ctx.quadraticCurveTo(x - r * 0.98, y + r * 0.48, x - r * 0.8, y - r * 0.12)
  ctx.quadraticCurveTo(x - r * 0.3, y - r * 0.76, x, y - r * 1.06)
  ctx.closePath()
}

export const paintKnoblauch: SpeciesPainter = (p: PaintParams) => {
  const { ctx, x, y, r, colors, tier, pose } = p
  const dead = p.emotion === 'dead'
  const outlineW = Math.max(2, r * 0.12)
  ctx.save()
  ctx.lineWidth = outlineW
  ctx.strokeStyle = colors.outline
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'

  // --- Schicht 1: hinterer Papier-Mantel — breiter, unten in Zipfel aufgefaechert ---
  {
    const g = ctx.createRadialGradient(x - r * 0.3, y - r * 0.2, r * 0.15, x, y + r * 0.2, r * 1.25)
    g.addColorStop(0, lighten(colors.bodyDark, 6))
    g.addColorStop(1, darken(colors.bodyDark, 12))
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.moveTo(x, y - r * 0.88)
    ctx.quadraticCurveTo(x + r * 0.7, y - r * 0.55, x + r * 0.92, y + r * 0.18)
    ctx.quadraticCurveTo(x + r * 0.94, y + r * 0.55, x + r * 0.76, y + r * 0.76)
    // Zipfel-Saum: Spitzen schauen unter der Haupt-Robe hervor ("unten leicht aufgefaechert")
    ctx.lineTo(x + r * 0.55, y + r * 0.97)
    ctx.lineTo(x + r * 0.4, y + r * 0.8)
    ctx.lineTo(x + r * 0.14, y + r * 0.99)
    ctx.lineTo(x - r * 0.08, y + r * 0.8)
    ctx.lineTo(x - r * 0.34, y + r * 0.96)
    ctx.lineTo(x - r * 0.52, y + r * 0.79)
    ctx.lineTo(x - r * 0.76, y + r * 0.76)
    ctx.quadraticCurveTo(x - r * 0.94, y + r * 0.55, x - r * 0.92, y + r * 0.18)
    ctx.quadraticCurveTo(x - r * 0.7, y - r * 0.55, x, y - r * 0.88)
    ctx.closePath()
    ctx.fill()
    ctx.stroke()
  }

  // --- Schicht 2: Haupt-Robe (Zwiebelform) mit weichem Volumen ---
  {
    // handgebauter Gradient: Lichtkern hoeher (Kopfbereich), warmer Kern, weicher Fall
    const g = ctx.createRadialGradient(x - r * 0.32, y - r * 0.5, r * 0.1, x, y + r * 0.05, r * 1.3)
    g.addColorStop(0, lighten(colors.body, 16))
    g.addColorStop(0.5, colors.body)
    g.addColorStop(1, darken(colors.body, 18))
    ctx.fillStyle = g
    robePath(ctx, x, y, r)
    ctx.fill()
    ctx.stroke()
  }

  // --- Schicht 3: Falten/Zehen-Linien mit Papier-Highlight-Kanten ---
  // (Knoblauch-Zehen als Robenfalten: vom Scheitel nach unten-aussen auffaechernd)
  ctx.save()
  const foldAlpha = dead ? 0.35 : 0.55
  for (const s of [-1, 1] as const) {
    // Highlight-Kante (Licht oben-links -> helle Papierkante links der Falte)
    ctx.strokeStyle = rgba(lighten(colors.body, 26), dead ? 0.2 : 0.65)
    ctx.lineWidth = Math.max(1, r * 0.045)
    ctx.beginPath()
    ctx.moveTo(x + s * r * 0.12 - r * 0.05, y - r * 0.9)
    ctx.quadraticCurveTo(x + s * r * 0.86 - r * 0.05, y - r * 0.28, x + s * r * 0.57 - r * 0.05, y + r * 0.8)
    ctx.stroke()
    // Falten-Schattenlinie
    ctx.strokeStyle = rgba(darken(colors.bodyDark, 8), foldAlpha)
    ctx.lineWidth = Math.max(1, r * 0.055)
    ctx.beginPath()
    ctx.moveTo(x + s * r * 0.12, y - r * 0.9)
    ctx.quadraticCurveTo(x + s * r * 0.86, y - r * 0.28, x + s * r * 0.57, y + r * 0.8)
    ctx.stroke()
    // kurze Aussenfalte (subtile zweite Papierlage an der Flanke)
    ctx.strokeStyle = rgba(darken(colors.bodyDark, 4), foldAlpha * 0.6)
    ctx.lineWidth = Math.max(1, r * 0.045)
    ctx.beginPath()
    ctx.moveTo(x + s * r * 0.52, y - r * 0.42)
    ctx.quadraticCurveTo(x + s * r * 0.88, y + r * 0.06, x + s * r * 0.72, y + r * 0.6)
    ctx.stroke()
  }
  ctx.restore()

  // --- Sternstufe 2+: Kordel-Guertel + Kreuz-Brosche + Saum-Borte ("bessere Robe") ---
  if (tier >= 2 && !dead) {
    const beltY = y + r * 0.6
    // Kordel (schmales, helles Seilband — darf den Mundbereich nicht bedraengen)
    ctx.save()
    const g = ctx.createLinearGradient(x, beltY - r * 0.08, x, beltY + r * 0.08)
    g.addColorStop(0, lighten(colors.accent, 18))
    g.addColorStop(0.5, colors.accent)
    g.addColorStop(1, darken(colors.accent, 12))
    ctx.fillStyle = g
    ctx.strokeStyle = colors.outline
    ctx.lineWidth = Math.max(1, r * 0.04)
    ctx.beginPath()
    ctx.ellipse(x, beltY, r * 0.68, r * 0.1, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.stroke()
    // Twist-Striche: gedrehte Seil-Optik (deterministisch verteilt)
    ctx.strokeStyle = rgba(darken(colors.accent, 24), 0.55)
    ctx.lineWidth = Math.max(1, r * 0.03)
    for (const dx of [-0.52, -0.34, -0.17, 0.17, 0.34, 0.52]) {
      ctx.beginPath()
      ctx.moveTo(x + dx * r, beltY - r * 0.08 + Math.abs(dx) * r * 0.05)
      ctx.lineTo(x + (dx + 0.08) * r, beltY + r * 0.08 - Math.abs(dx) * r * 0.05)
      ctx.stroke()
    }
    // Metall-Brosche mit Kreuz-Gravur (Priester-Signet, Metallpalette wie kohlrabi-Band)
    const bg = ctx.createLinearGradient(x, beltY - r * 0.12, x, beltY + r * 0.12)
    bg.addColorStop(0, '#d7dee6')
    bg.addColorStop(0.55, '#9aa4ae')
    bg.addColorStop(1, '#6a737d')
    ctx.fillStyle = bg
    ctx.strokeStyle = colors.outline
    ctx.lineWidth = Math.max(1.5, r * 0.05)
    ctx.beginPath()
    ctx.arc(x, beltY, r * 0.12, 0, Math.PI * 2)
    ctx.fill()
    ctx.stroke()
    ctx.strokeStyle = tier >= 3 ? HOLY_GOLD : '#3f464e'
    ctx.lineWidth = Math.max(1.5, r * 0.04)
    ctx.beginPath()
    ctx.moveTo(x, beltY - r * 0.07)
    ctx.lineTo(x, beltY + r * 0.07)
    ctx.moveTo(x - r * 0.055, beltY - r * 0.015)
    ctx.lineTo(x + r * 0.055, beltY - r * 0.015)
    ctx.stroke()
    ctx.restore()
    // Saum-Borte: Stickerei entlang der Scallops
    ctx.save()
    ctx.strokeStyle = rgba(tier >= 3 ? HOLY_GOLD : darken(colors.accent, 12), 0.85)
    ctx.lineWidth = Math.max(1, r * 0.05)
    ctx.beginPath()
    ctx.moveTo(x + r * 0.6, y + r * 0.74)
    ctx.quadraticCurveTo(x + r * 0.41, y + r * 0.89, x + r * 0.21, y + r * 0.75)
    ctx.quadraticCurveTo(x + r * 0.01, y + r * 0.91, x - r * 0.19, y + r * 0.76)
    ctx.quadraticCurveTo(x - r * 0.42, y + r * 0.88, x - r * 0.6, y + r * 0.74)
    ctx.stroke()
    ctx.restore()
  }

  // --- Sternstufe 3: leuchtende Segens-Runen auf der Robe (docs/11 "leuchtende Runen") ---
  if (tier >= 3 && !dead) {
    const runeAlpha = 0.78 + pose.castPulse * 0.22
    ctx.save()
    ctx.strokeStyle = rgba(HOLY_GOLD, runeAlpha)
    ctx.lineWidth = Math.max(1.5, r * 0.06)
    ctx.shadowColor = HOLY_GOLD
    ctx.shadowBlur = r * (0.2 + pose.castPulse * 0.12)
    // links: Segenswelle (Heilung — zwei sanfte Bogen)
    ctx.beginPath()
    ctx.moveTo(x - r * 0.66, y + r * 0.24)
    ctx.quadraticCurveTo(x - r * 0.56, y + r * 0.14, x - r * 0.46, y + r * 0.24)
    ctx.moveTo(x - r * 0.66, y + r * 0.36)
    ctx.quadraticCurveTo(x - r * 0.56, y + r * 0.26, x - r * 0.46, y + r * 0.36)
    ctx.stroke()
    // rechts: Strahl-Rune (aufsteigendes Licht)
    ctx.beginPath()
    ctx.moveTo(x + r * 0.56, y + r * 0.38)
    ctx.lineTo(x + r * 0.56, y + r * 0.12)
    ctx.moveTo(x + r * 0.47, y + r * 0.3)
    ctx.lineTo(x + r * 0.56, y + r * 0.18)
    ctx.lineTo(x + r * 0.65, y + r * 0.3)
    ctx.stroke()
    ctx.restore()
  }

  // --- Heiligenschein (3★): hintere Haelfte HINTER dem Spross ---
  const haloY = y - r * 1.14
  const haloRx = r * 0.42
  const haloRy = r * 0.11
  const haloAlpha = 0.75 + pose.castPulse * 0.25
  const drawHaloArc = (from: number, to: number): void => {
    ctx.save()
    ctx.strokeStyle = rgba(HOLY_GOLD, haloAlpha)
    ctx.lineWidth = Math.max(2, r * 0.07)
    ctx.shadowColor = HOLY_GOLD
    ctx.shadowBlur = r * (0.25 + pose.castPulse * 0.15)
    ctx.beginPath()
    ctx.ellipse(x, haloY, haloRx, haloRy, 0, from, to)
    ctx.stroke()
    ctx.restore()
  }
  if (tier >= 3 && !dead) drawHaloArc(Math.PI, Math.PI * 2)

  // --- Spross-Tonsur oben (Halo-Traeger): Papier-Kaeppchen + kleiner gebogener Trieb ---
  {
    const tipY = y - r * 1.02
    // Papier-Kaeppchen (hellere Wickel-Spitze)
    ctx.fillStyle = lighten(colors.body, dead ? 4 : 12)
    ctx.lineWidth = Math.max(1.5, r * 0.08)
    ctx.beginPath()
    ctx.moveTo(x - r * 0.16, tipY + r * 0.14)
    ctx.quadraticCurveTo(x - r * 0.06, tipY - r * 0.08, x + r * 0.02, tipY - r * 0.12)
    ctx.quadraticCurveTo(x + r * 0.13, tipY - r * 0.04, x + r * 0.16, tipY + r * 0.14)
    ctx.quadraticCurveTo(x, tipY + r * 0.22, x - r * 0.16, tipY + r * 0.14)
    ctx.closePath()
    ctx.fill()
    ctx.stroke()
    // kleiner Trieb, sanft zur Seite geneigt (freundlich, nicht zackig)
    const sg = ctx.createLinearGradient(x, tipY - r * 0.3, x, tipY)
    sg.addColorStop(0, lighten(colors.accent, 26))
    sg.addColorStop(1, colors.accent)
    ctx.strokeStyle = sg
    ctx.lineWidth = Math.max(2, r * 0.085)
    ctx.beginPath()
    ctx.moveTo(x + r * 0.01, tipY - r * 0.08)
    ctx.quadraticCurveTo(x + r * 0.02, tipY - r * 0.22, x + r * 0.12, tipY - r * 0.28)
    ctx.stroke()
    ctx.strokeStyle = colors.outline
  }

  // --- Heiligenschein (3★): vordere Haelfte VOR dem Spross (Ring umschliesst ihn) ---
  if (tier >= 3 && !dead) drawHaloArc(0, Math.PI)

  // --- Licht: warmes, weiches Doppel-Rim (Gold) + Speculars ---
  if (!dead) {
    // breiter, sehr weicher Schein (sanfter Beschuetzer)
    rimLight(ctx, x, y - r * 0.04, r * 0.84, r * 0.94, 'rgba(255, 236, 190, 0.28)', Math.max(2.5, r * 0.15))
    // crisper goldener Kern-Rim (staerker als Standard, Gold-Ton)
    rimLight(ctx, x, y - r * 0.04, r * 0.84, r * 0.94, 'rgba(255, 224, 150, 0.6)', Math.max(1.5, r * 0.075))
    // Glanzpunkt auf der Kopf-Rundung + wachsiger Seiten-Sheen auf dem Bauch
    specular(ctx, x - r * 0.38, y - r * 0.6, r * 0.14, 0.5)
    specular(ctx, x - r * 0.48, y + r * 0.28, r * 0.26, 0.12)
  }

  ctx.restore()
}
